
/**
 * Created by rhinov8e on 20/11/2015.
 *
 * UI State generic from which all the others inherit
 */

import { ActionStateEnum, UiIdleState, CutAndRulerState, VisualisePlanState, EntityDimensionState, SelectableElementsState, CutDimensionState, RulerState, RoomMagnetState, DragCoatingState, EditPointState, AreaState, EditWallState, EditJoineryState, AddJoineryState, AddRoomState } from "..";
import { EntityManager } from "../../plan/EntityManager";
import { Utils } from "../../utils/Utils";
import { PlanManager } from "../../../../cocosPlan";
import { ArrangementGroup, arrangementManager, ArrangementObject, Entity, Event, Events, eventsManager, Joinery, joineryManager, JSONSerializer, math, planManager, RenderCamera, Room, roomManager, SavaneConstants, SavaneMath, SceneConstants, Segment, Staircase, TechnicalElement, Wall, wallManager } from "@rhinov/savane-js";

declare let cc: any;


// UiState enum so we can know the current state we are in
export const UiStateEnum =
    {
        uiIdleState: 0,
        uiRulerState: 1,
        uiCutDimensionState: 2,
        uiCutAndRulerState: 3,
        uiRoomMagnetState: 4,
        uiEntityDimension: 5,
        uiSelectableElements: 6,
        uiVisualisePlanState: 7,
        UiFrontView: 8
    };

// Search for entities we will perform below
export const UiSearchEntity =
    {
        searchWallsWithoutCorner: 0,
        searchWalls: 1,
        searchUserPicture: 2,
        searchRenderCamera: 3,
        searchComment: 5,
        searchGeometryPrimitive: 6,
        searchArrangements: 7,
        searchTechnicalElement: 9,
        searchJoinery: 10,
        searchRoom: 11,
        searchFunctionnalityChip: 12,
        searchPhotoRenderCamera: 13,
        searchAdditionalRenderCamera: 14
    };

// Hover type of the hover entity
export const HoverType =
    {
        noEntities: 0,
        savaneEntities: 1
    };

export class UiState {

     // Entity Non Temporary at the mouse position
    hoverEntities: Entity[] = [];

    // hoverEntities is not only savane entities , so we stock the type...
    hoverType: number = HoverType.noEntities;

    // Do we display vertical and horizontal rulers
    verticalRuler: boolean = false;
    horizontalRuler: boolean = false;
    // Cursor position, this one is snapped
    cursorPosition: math.vec3 = math.vec3.create();
    // Unsapped cursor used for some state like arrangement and technical element moves (they are sometimes too thin to be touched with the snap of walls)
    unsnappedCursorPosition: math.vec3 = math.vec3.create();
    // Cocos cursor position
    cocosPosition: math.vec3 | null = null;
    orientation: boolean = true;
    // No ctrl or alt pressed yet
    ctrlPressed: boolean = false;
    altPressed: boolean = false;
    deepPressed: boolean = false;

    _rulersToggle = true;

    constructor(cursorPosition: math.vec3 | null, cocosPosition: math.vec3 |null) {

        // Copy cocos and cursor pos if they exist
        if (cursorPosition && cocosPosition) {
            this.cocosPosition = cocosPosition;
            math.vec3.copy(this.cursorPosition, cursorPosition);
            math.vec3.copy(this.unsnappedCursorPosition, cursorPosition);
            this.setSelected();
        }
    }

     // Activation/Deactivation of rulers
    toggleRulers(event: Event) {
        this._rulersToggle = event.getUserData();
    }

    // Returns the next UiState to go to depending on current action
    getNextUiState(): number {
        // Plan manager and plan manager action shortcuts
        let cocosplanManager = PlanManager.getInstance();
        let cocosplanManagerAction = cocosplanManager.getAction();

        // Copy unsnapped cursor position now (before snap)
        math.vec3.copy(this.unsnappedCursorPosition, this.cursorPosition);

        // Cursor choice
        switch (cocosplanManagerAction) {
            case ActionStateEnum.idleCreate:
            case ActionStateEnum.addRoom:
            case ActionStateEnum.idleMultipleCreates:
            case ActionStateEnum.DrawArea:
            case ActionStateEnum.DrawWorkTop:
            case ActionStateEnum.createMultipleWalls:
            case ActionStateEnum.FloorCoatingArea:
                const canvasEl = document.getElementById('gameCanvas');
                if (canvasEl) {
                    canvasEl.style.cursor = 'crosshair';
                }
                break;

            case ActionStateEnum.DragCoating:
                (cocosplanManager.state as DragCoatingState).setEditionCursor();
                break;

            default:
                const canvasEl2 = document.getElementById('gameCanvas');
                if (canvasEl2) {
                    canvasEl2.style.cursor = 'default';
                }
        }

        // State selection
        switch (cocosplanManagerAction) {
            case ActionStateEnum.idleVisualise:
            case ActionStateEnum.RoomsValidation:
            case ActionStateEnum.SelectedOrderRoom:
            case ActionStateEnum.AbsoluteIdle:
            case ActionStateEnum.Quotation:
            case ActionStateEnum.idleCamera:
            case ActionStateEnum.MoveFunctionalityChipState:
                return UiStateEnum.uiVisualisePlanState;

            case ActionStateEnum.addJoinery:
            case ActionStateEnum.editJoinery:
            case ActionStateEnum.DrawArea:
            case ActionStateEnum.DrawWorkTop:
            case ActionStateEnum.FloorCoatingArea:
                return UiStateEnum.uiIdleState;

            case ActionStateEnum.IdleConstruction:
            case ActionStateEnum.idleDecoration:
                return UiStateEnum.uiSelectableElements;

            case ActionStateEnum.EditTechnicalElement:
            case ActionStateEnum.AddTechnicalElement:
            case ActionStateEnum.MoveTechnicalElement:
            case ActionStateEnum.EditStaircase:
            case ActionStateEnum.AddStaircase:
                return UiStateEnum.uiEntityDimension;

            case ActionStateEnum.AddEntity:
            case ActionStateEnum.EditEntity:
                if (cocosplanManager.getActionEntityType() !== SceneConstants.EntityType.RenderCamera &&
                    cocosplanManager.getActionEntityType() !== SceneConstants.EntityType.UserPicture &&
                    cocosplanManager.getActionEntityType() !== SceneConstants.EntityType.Comment) {
                    return UiStateEnum.uiEntityDimension;
                }
                else {
                    return UiStateEnum.uiIdleState
                }

            case ActionStateEnum.editWall:
                return UiStateEnum.uiIdleState;

            default:
                if (this.hoverType === HoverType.savaneEntities && this.hoverEntities.length === 1 && this.hoverEntities[0].isWallEntity()) {
                    let cutDimension = true;

                    if (cocosplanManagerAction === ActionStateEnum.editPoint) {
                        if ((this.hoverEntities[0] as Wall).isCorner((cocosplanManager.state as EditPointState).editedPoint)) {
                            cutDimension = false;
                        }
                    }

                    if ((this.hoverEntities[0] as Wall).isCorner(this.cursorPosition)) {
                        cutDimension = false;
                    }

                    if (cutDimension) {
                        if (this.verticalRuler || this.horizontalRuler) {
                            return UiStateEnum.uiCutAndRulerState;
                        }
                        else {
                            return UiStateEnum.uiCutDimensionState;
                        }
                    }
                    else {
                        if (this.verticalRuler || this.horizontalRuler) {
                            return UiStateEnum.uiRulerState;
                        }
                        return UiStateEnum.uiIdleState;
                    }
                }
                else {
                    if (this.verticalRuler || this.horizontalRuler) {
                        return UiStateEnum.uiRulerState;
                    }
                }
        }

        return UiStateEnum.uiIdleState;
    }

    // Returns the next UiState depending on the next state returned by getNextUiState
    createNextUiState(): UiState {
        // Close current UiState
        this.dispose();

        // Get next state to go to for next round
        let nextState = this.getNextUiState();

        // Create new state and return it
        switch (nextState) {
            case UiStateEnum.uiSelectableElements:
                return new SelectableElementsState(this.cursorPosition, this.cocosPosition!);

            case UiStateEnum.uiCutDimensionState:
                return new CutDimensionState(this.cursorPosition, this.cocosPosition!, this.hoverEntities[0] as Wall);

            case UiStateEnum.uiRulerState:
                return new RulerState(this.cursorPosition, this.cocosPosition!);

            case UiStateEnum.uiRoomMagnetState:
                return new RoomMagnetState(this.cursorPosition, this.cocosPosition!);

            case UiStateEnum.uiCutAndRulerState:
                return new CutAndRulerState(this.cursorPosition, this.cocosPosition!, this.hoverEntities[0] as Wall);

            case UiStateEnum.uiEntityDimension:
                return new EntityDimensionState(this.cursorPosition, this.cocosPosition!);

            case UiStateEnum.uiVisualisePlanState:
                return new VisualisePlanState(this.cursorPosition, this.cocosPosition!);

            default:
                return new UiIdleState(this.cursorPosition, this.cocosPosition!);
        }
    }

    // Determines if an object (whatever arrangement, room, wall, camera...) is under the mouse cursor
    setSelected() {
        // Plan manager and plan manager action shortcuts
        let cocosplanManager = PlanManager.getInstance();
        let cocosplanManagerAction = cocosplanManager.getAction();

        if (!cocosplanManager.world) {
            return;
        }

        if (cocosplanManagerAction === ActionStateEnum.DrawWorkTop) {
            return;
        }

        if (cocosplanManagerAction === ActionStateEnum.DrawArea) {
            //magnet on inner room point extremities
            let firstVertex = (cocosplanManager.state as AreaState).getFirstVertex();
            if (firstVertex !== null && math.vec3.distance(this.cursorPosition, firstVertex) < Utils.magnetDistance) {
                this.cursorPosition = firstVertex;
            } else {
                let editedSegment: Segment, dist: number, ratio: number;
                //angle magnet
                let firstPoint = (cocosplanManager.state as AreaState).beginPoint;
                let lastSegment = (cocosplanManager.state as AreaState).lastSegment;

                if ((cocosplanManager.state as AreaState).isEditing()) {
                    let tmpPosition = math.vec3.clone(this.cursorPosition);
                    const currentFloor = cocosplanManager.world.currentScene?.currentFloor;
                    if (currentFloor) {
                        this.cursorPosition = planManager.getMagnetPositionInsideRoom(this.cursorPosition, Utils.magnetDistance, Utils.magnetDistance, currentFloor);
                    }

                    if (firstVertex !== null) {
                        if (firstPoint !== null && lastSegment !== null && !this.ctrlPressed) {
                            editedSegment = new Segment(firstPoint, this.cursorPosition);
                            dist = editedSegment.length;
                            ratio = 2500 / dist > 1 ? 2500 / dist : 1;
                            if (dist > 100 && Math.abs(math.vec3.dot(editedSegment.direction, lastSegment.direction)) < Utils.angleMagnet * ratio) {
                                if (math.vec3.distance(tmpPosition, this.cursorPosition) < SavaneConstants.PositionTolerance) {
                                    if (editedSegment.direction[0] < 0) {
                                        this.cursorPosition[0] = -Math.abs(lastSegment.direction[1]) * dist + editedSegment.begin[0];
                                    } else {
                                        this.cursorPosition[0] = Math.abs(lastSegment.direction[1]) * dist + editedSegment.begin[0];
                                    }
                                    if (editedSegment.direction[1] < 0) {
                                        this.cursorPosition[1] = -Math.abs(lastSegment.direction[0]) * dist + editedSegment.begin[1];
                                    } else {
                                        this.cursorPosition[1] = Math.abs(lastSegment.direction[0]) * dist + editedSegment.begin[1];
                                    }
                                } else {
                                    const floor = cocosplanManager.world.currentScene?.currentFloor;
                                    let room = floor ? roomManager.getRoomAtPosition(this.cursorPosition, floor) : null;
                                    if (room !== null) {
                                        //get Inner room segment to get cross point between them and magnetised segment!
                                        let points = room.getInnerPointsList(false);
                                        let toAngleSnap = true;
                                        for (let i = 0; i < points.length; i++) {
                                            if (math.vec3.dist(points[i], this.cursorPosition) < Utils.magnetDistance) {
                                                toAngleSnap = false;
                                            }
                                        }
                                        if (toAngleSnap) {
                                            let testSegment = new Segment(math.vec3.create(), math.vec3.create());
                                            for (let i = 0; i < points.length; i++) {
                                                testSegment.begin = points[i];
                                                testSegment.end = points[(i + 1) % points.length];

                                                if (testSegment.isPointOnSegment(this.cursorPosition, Utils.magnetDistance)) {
                                                    let lastPoint = math.vec3.clone(firstPoint);
                                                    lastPoint[0] += -(lastSegment.direction[1]) * dist;
                                                    lastPoint[1] += (lastSegment.direction[0]) * dist;
                                                    let snapPoint = SavaneMath.getCrossPoint(testSegment.begin, testSegment.end, firstPoint, lastPoint);
                                                    if (snapPoint !== null && math.vec3.distance(snapPoint, firstPoint) > SavaneConstants.PositionTolerance) {
                                                        this.cursorPosition = snapPoint;
                                                    }
                                                    break;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        } else if (math.vec3.distance(this.cursorPosition, firstVertex) > Utils.magnetDistance && !this.ctrlPressed) {
                            editedSegment = new Segment(firstVertex, this.cursorPosition);
                            dist = editedSegment.length;
                            ratio = 2500 / dist > 1 ? 2500 / dist : 1;
                            let verticalDirection = math.vec3.create();
                            math.vec3.set(verticalDirection, 0, 1, 0);
                            let horizontalDirection = math.vec3.create();
                            math.vec3.set(horizontalDirection, 1, 0, 0);
                            if (dist > 100 && Math.abs(math.vec3.dot(editedSegment.direction, verticalDirection)) < Utils.angleMagnet * ratio) {
                                this.cursorPosition[1] = editedSegment.begin[1];
                            }
                            if (dist > 100 && Math.abs(math.vec3.dot(editedSegment.direction, horizontalDirection)) < Utils.angleMagnet * ratio) {
                                this.cursorPosition[0] = editedSegment.begin[0];
                            }
                        }
                    } else {
                        const areaState = (cocosplanManager.state as AreaState);
                        if (areaState._editedPointIndex !== null && areaState._editedPointIndex !== undefined && areaState._area?.vertices) {
                            // @here testing 90 degree snap when zone is closed
                            const vlen = areaState._area.vertices.length;
                            let previousPoint = areaState._area.vertices[((areaState._editedPointIndex - 1 + vlen) % vlen)];
                            let previousPreviousPoint = areaState._area.vertices[((areaState._editedPointIndex - 2 + vlen) % vlen)];
                            let nextPoint = areaState._area.vertices[((areaState._editedPointIndex + 1) % vlen)];
                            let nextNextPoint = areaState._area.vertices[((areaState._editedPointIndex + 2) % vlen)];

                            if (previousPoint && previousPreviousPoint && nextPoint && nextNextPoint) {
                                let editedSegment1 = new Segment(previousPoint, this.cursorPosition);
                                let editedSegment2 = new Segment(nextPoint, this.cursorPosition);
                                let previousSegment = new Segment(previousPoint, previousPreviousPoint);
                                let nexSegment = new Segment(nextPoint, nextNextPoint);

                            let dist1 = editedSegment1.length;
                            let dist2 = editedSegment2.length;
                            let ratio1 = 2500 / dist1 > 1 ? 2500 / dist1 : 1;
                            let ratio2 = 2500 / dist2 > 1 ? 2500 / dist2 : 1;

                            let a, a0, a1, b, b0, b1;
                            a = false;
                            b = false;

                            let tmpPointA = math.vec3.create();
                            let tmpPointB = math.vec3.create();

                            // previous segment
                            if (dist1 > 100 && Math.abs(math.vec3.dot(editedSegment1.direction, previousSegment.direction)) < Utils.angleMagnet * ratio1) {
                                if (math.vec3.distance(tmpPosition, this.cursorPosition) < SavaneConstants.PositionTolerance) {
                                    a = true;
                                    if (editedSegment1.direction[0] < 0) {
                                        a0 = true;
                                        tmpPointA[0] = -Math.abs(previousSegment.direction[1]) * dist1 + editedSegment1.begin[0];
                                    } else {
                                        a0 = false;
                                        tmpPointA[0] = Math.abs(previousSegment.direction[1]) * dist1 + editedSegment1.begin[0];
                                    }
                                    if (editedSegment1.direction[1] < 0) {
                                        tmpPointA[1] = -Math.abs(previousSegment.direction[0]) * dist1 + editedSegment1.begin[1];
                                        a1 = true;
                                    } else {
                                        tmpPointA[1] = Math.abs(previousSegment.direction[0]) * dist1 + editedSegment1.begin[1];
                                        a1 = false;
                                    }
                                }
                            }

                            // Next segment
                            if (dist2 > 100 && Math.abs(math.vec3.dot(editedSegment2.direction, nexSegment.direction)) < Utils.angleMagnet * ratio2) {
                                if (math.vec3.distance(tmpPosition, this.cursorPosition) < SavaneConstants.PositionTolerance) {
                                    b = true;
                                    if (editedSegment2.direction[0] < 0) {
                                        tmpPointB[0] = -Math.abs(nexSegment.direction[1]) * dist2 + editedSegment2.begin[0];
                                        b0 = true;
                                    } else {
                                        tmpPointB[0] = Math.abs(nexSegment.direction[1]) * dist2 + editedSegment2.begin[0];
                                        b0 = false;
                                    }
                                    if (editedSegment2.direction[1] < 0) {
                                        tmpPointB[1] = -Math.abs(nexSegment.direction[0]) * dist2 + editedSegment2.begin[1];
                                        b1 = true;
                                    } else {
                                        tmpPointB[1] = Math.abs(nexSegment.direction[0]) * dist2 + editedSegment2.begin[1];
                                        b1 = false;
                                    }
                                }
                            }

                            if (a && !b) {
                                if (a0) {
                                    this.cursorPosition[0] = -Math.abs(previousSegment.direction[1]) * dist1 + editedSegment1.begin[0];
                                }
                                else {
                                    this.cursorPosition[0] = Math.abs(previousSegment.direction[1]) * dist1 + editedSegment1.begin[0];
                                }
                                if (a1) {
                                    this.cursorPosition[1] = -Math.abs(previousSegment.direction[0]) * dist1 + editedSegment1.begin[1];
                                }
                                else {
                                    this.cursorPosition[1] = Math.abs(previousSegment.direction[0]) * dist1 + editedSegment1.begin[1];
                                }
                            }
                            else if (b && !a) {
                                if (b0) {
                                    this.cursorPosition[0] = -Math.abs(nexSegment.direction[1]) * dist2 + editedSegment2.begin[0];
                                }
                                else {
                                    this.cursorPosition[0] = Math.abs(nexSegment.direction[1]) * dist2 + editedSegment2.begin[0];
                                }
                                if (b1) {
                                    this.cursorPosition[1] = -Math.abs(nexSegment.direction[0]) * dist2 + editedSegment2.begin[1];
                                }
                                else {
                                    this.cursorPosition[1] = Math.abs(nexSegment.direction[0]) * dist2 + editedSegment2.begin[1];
                                }
                            }
                                else if (a && b) {
                                    let snapPoint = SavaneMath.getCrossPoint(previousPoint, tmpPointA, nextPoint, tmpPointB);
                                    if (snapPoint !== null) {
                                        this.cursorPosition = snapPoint;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        } else if (cocosplanManagerAction !== ActionStateEnum.editWall) {
            //cursor magnet
            let tmpPosition = math.vec3.clone(this.cursorPosition);
            if ((cocosplanManagerAction === ActionStateEnum.editJoinery && (cocosplanManager.state as EditJoineryState)._editLength)) {
                this.cursorPosition = planManager.getMagnetPositionEntityEdit(this.cursorPosition, Utils.magnetDistance, (cocosplanManager.state as EditJoineryState)._editedJoinery.parent as Wall);
            } else if (cocosplanManagerAction === ActionStateEnum.addJoinery || cocosplanManagerAction === ActionStateEnum.editJoinery) {
                if ((cocosplanManagerAction === ActionStateEnum.addJoinery && (cocosplanManager.state as AddJoineryState)._joinery.joineryType !== SceneConstants.JoineryType.velux) ||
                    (cocosplanManagerAction === ActionStateEnum.editJoinery && (cocosplanManager.state as EditJoineryState)._editedJoinery.joineryType !== SceneConstants.JoineryType.velux)) {
                    const currentFloor = cocosplanManager.world.currentScene?.currentFloor;
                    if (currentFloor) {
                        this.cursorPosition = planManager.getMagnetPositionOnNonTemporaryEntity(this.cursorPosition, Utils.magnetDistance, Utils.magnetDistance, currentFloor);
                    }
                }
            } else if (cocosplanManagerAction !== ActionStateEnum.EditEntity
                && cocosplanManagerAction !== ActionStateEnum.AddEntity
                // ETT change to remove if no side effect
                /*&& cocosplanManagerAction !== ActionStateEnum.idleDecoration*/
                && cocosplanManagerAction !== ActionStateEnum.SelectedOrderRoom
                && cocosplanManagerAction !== ActionStateEnum.idleCamera
                && (cocosplanManagerAction !== ActionStateEnum.EditStaircase /*|| cocosplanManager.state.editMode !== EditModeEnum.resize*/)) {
                const currentFloor = cocosplanManager.world.currentScene?.currentFloor;
                if (currentFloor) {
                    this.cursorPosition = planManager.getMagnetPositionOnNonTemporaryEntity(this.cursorPosition, Utils.magnetDistance, Utils.magnetDistance, currentFloor);
                }
            }

            // Set hovered object and unset object leaving hovered state
            this.setHover();

            if (this.hoverType === HoverType.savaneEntities && this.hoverEntities.length === 1 && this.hoverEntities[0] instanceof Wall &&
                (cocosplanManagerAction === ActionStateEnum.addJoinery || cocosplanManagerAction === ActionStateEnum.editJoinery)) {
                let direction = math.vec3.create();
                math.vec3.set(direction, tmpPosition[0] - this.hoverEntities[0].begin[0], tmpPosition[1] - this.hoverEntities[0].begin[1], 0);
                let length = math.vec3.length(direction);
                math.vec3.set(direction, direction[0] / length, direction[1] / length, 0);
                let angle = Math.sin(direction[0] * this.hoverEntities[0].wallDirection[1] - direction[1] * this.hoverEntities[0].wallDirection[0]) *
                    Math.acos(direction[0] * this.hoverEntities[0].wallDirection[0] + direction[1] * this.hoverEntities[0].wallDirection[1]);
                if (angle < 0) {
                    this.orientation = false;
                } else {
                    this.orientation = true;
                }
            }

            if ((this.hoverType !== HoverType.savaneEntities || this.hoverEntities.length === 0 ||
                (!this.hoverEntities[0].isJoineryEntity() && (!this.hoverEntities[0].isWallEntity() || cocosplanManagerAction !== ActionStateEnum.IdleConstruction))) &&
                cocosplanManagerAction !== ActionStateEnum.editJoinery && cocosplanManagerAction !== ActionStateEnum.addJoinery &&
                cocosplanManagerAction !== ActionStateEnum.EditEntity && cocosplanManagerAction !== ActionStateEnum.AddEntity &&
                cocosplanManagerAction !== ActionStateEnum.DragCoating &&
                cocosplanManagerAction !== ActionStateEnum.AddTechnicalElement && this._rulersToggle) {
                this.setRuler();
            }

            // angle magnetisation
            if ((cocosplanManagerAction === ActionStateEnum.createWall || cocosplanManagerAction === ActionStateEnum.editPoint) && (cocosplanManager.state as EditPointState).beginPoint !== null) {
                let firstPoint = (cocosplanManager.state as EditPointState).beginPoint!;
                if (math.vec3.distance(this.cursorPosition, firstPoint) === 0) {
                    this.cursorPosition = tmpPosition;
                }
                const currentFloor = cocosplanManager.world.currentScene?.currentFloor;
                if (!currentFloor) {
                    return;
                }
                let walls = wallManager.getWallsAtPosition(firstPoint, currentFloor, SavaneConstants.PositionTolerance, -1);

                let editedSegment = new Segment(firstPoint, this.cursorPosition);

                let normal1 = math.vec3.create();
                math.vec3.set(normal1, -editedSegment.direction[1], editedSegment.direction[0], 0);
                math.vec3.normalize(normal1, normal1);
                let normal2 = math.vec3.create();
                math.vec3.set(normal2, editedSegment.direction[1], -editedSegment.direction[0], 0);
                math.vec3.normalize(normal2, normal2);

                if (walls !== null) {
                    for (let i = walls.length - 1; i >= 0; i--) {
                        if (!walls[i].isTemporary()) {
                            if (walls !== null) {
                                let direction1 = walls[i].wallDirection;
                                let direction2 = math.vec3.create();
                                let dist = math.vec3.distance(firstPoint, this.cursorPosition);
                                math.vec3.set(direction2, this.cursorPosition[0] - firstPoint[0], this.cursorPosition[1] - firstPoint[1], this.cursorPosition[2] - firstPoint[2]);
                                math.vec3.normalize(direction2, direction2);
                                let ratio = 2500 / dist > 1 ? 2500 / dist : 1;
                                if (Math.abs(math.vec3.dot(direction1, direction2)) < Utils.angleMagnet * ratio) {
                                    if (math.vec3.distance(tmpPosition, this.cursorPosition) < SavaneConstants.PositionTolerance) {
                                        if (direction2[0] < 0) {
                                            this.cursorPosition[0] = -Math.abs(direction1[1]) * dist + firstPoint[0];
                                        } else {
                                            this.cursorPosition[0] = Math.abs(direction1[1]) * dist + firstPoint[0];
                                        }
                                        if (direction2[1] < 0) {
                                            this.cursorPosition[1] = -Math.abs(direction1[0]) * dist + firstPoint[1];
                                        } else {
                                            this.cursorPosition[1] = Math.abs(direction1[0]) * dist + firstPoint[1];
                                        }
                                    } else {
                                        if (this.hoverType === HoverType.savaneEntities && this.hoverEntities.length === 1 &&
                                            this.hoverEntities[0] instanceof Wall && !this.hoverEntities[0].isCorner(this.cursorPosition)) {

                                            let lastPoint = math.vec3.clone(firstPoint);
                                            lastPoint[0] += -(direction1[1]) * dist;
                                            lastPoint[1] += (direction1[0]) * dist;

                                            let vec = this.hoverEntities[0].wallDirection;
                                            let seg = new Segment(this.hoverEntities[0].begin, this.hoverEntities[0].end);

                                            seg.begin[0] = seg.begin[0] + vec[1] * -this.hoverEntities[0].shiftOffset;
                                            seg.begin[1] = seg.begin[1] + vec[0] * this.hoverEntities[0].shiftOffset;
                                            seg.end[0] = seg.end[0] + vec[1] * -this.hoverEntities[0].shiftOffset;
                                            seg.end[1] = seg.end[1] + vec[0] * this.hoverEntities[0].shiftOffset;

                                            let snapPoint = SavaneMath.getCrossPoint(seg.begin, seg.end, firstPoint, lastPoint);
                                            if (snapPoint !== null && math.vec3.distance(snapPoint, firstPoint) > SavaneConstants.PositionTolerance) {
                                                this.cursorPosition = snapPoint;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // Check collisions with entities others than the ones managed by savane managers
    collisionWithCursor(entityArray: Entity[], cursorPosition: math.vec3 | null): Entity[] {
        // Entity not sorted yet
        let entitiesUnsorted = [];
        // Entity to returned
        let entitiesToReturn = [];
        // Loop vairiable
        let i;

        // No array ==> No collision
        if (entityArray === null) {
            return ([]);
        }

        // Parse all array elements
        for (i = 0; i < entityArray.length; i++) {
            // Get cocos node
            let node = EntityManager.getInstance().getNodeById(entityArray[i].id);

            // Node found ? Check collision
            if (node !== null) {
                // Get node width and height for collision
                let halfNodeWidth = (node.getWidth() * node._sprite.getScaleX()) / 2;
                let halfNodeHeight = (node.getHeight() * node._sprite.getScaleY()) / 2;
                let convertedPos = node.convertToNodeSpace(cursorPosition);

                // If camera, reduce the collision box because camera sprites are big
                if (entityArray[i].isRenderCameraEntity() && (entityArray[i] as RenderCamera).cameraType !== SceneConstants.CameraType.Panoramic) {
                    halfNodeWidth /= 2;
                    halfNodeHeight /= 2;
                }

                // Check minimal size width and height and force them to a minimum size to be sure to be able to collide the element
                if (halfNodeWidth < 4) {
                    halfNodeWidth = 4;
                }
                if (halfNodeHeight < 4) {
                    halfNodeHeight = 4;
                }

                if (convertedPos.x > -halfNodeWidth && convertedPos.x < halfNodeWidth && convertedPos.y > -halfNodeHeight && convertedPos.y < halfNodeHeight) {
                    entitiesUnsorted.push(entityArray[i]);
                }
            }
        }

        // Sort entities thanks to their local Z order
        while (entitiesUnsorted.length !== 0) {
            let maxHeight = 0;
            let indexToAdd = 0;

            // Get cocos node
            let node = EntityManager.getInstance().getNodeById(entitiesUnsorted[0].id);

            // Node found ? Check collision
            if (node !== null) {
                indexToAdd = 0;
                maxHeight = node.getLocalZOrder();
            }

            for (i = 1; i < entitiesUnsorted.length; i++) {
                // Get cocos node
                node = EntityManager.getInstance().getNodeById(entitiesUnsorted[i].id);

                // Node found ? Check collision
                if (node !== null) {
                    // Return the top most object (the highest local Z order)
                    if (maxHeight <= node.getLocalZOrder()) {
                        indexToAdd = i;
                        maxHeight = node.getLocalZOrder();
                    }
                }
            }

            entitiesToReturn.push(entitiesUnsorted[indexToAdd]);
            entitiesUnsorted.splice(indexToAdd, 1);
        }

        return (entitiesToReturn);
    }

    // Search entities in the scene depending on an entity type searched
    searchEntity(typeArray: number[], cursorPosition: math.vec3, unsnappedCursorPosition: math.vec3, cocosPosition: math.vec3 | null): Entity[] {
        // Entities to return is an empty array for the moment
        let entities: Entity[] = [];
        // PlanManager shorcut
        let cocosplanManager = PlanManager.getInstance();
        // Current floor
        let currentFloor = cocosplanManager.world.currentScene?.currentFloor;
        if (!currentFloor) {
            return entities;
        }

        // Parse all types du check
        for (let i = 0; i < typeArray.length; i++) {
            // Depending on type to check
            switch (typeArray[i]) {
                // Walls without corner
                case UiSearchEntity.searchWallsWithoutCorner: {
                    // Get walls without corners
                    let walls = wallManager.getWallsAtPositionWithoutCorner(cursorPosition, currentFloor, SavaneConstants.PositionTolerance, -1);

                    // If any ?
                    if (walls !== null && walls.length !== 0) {
                        // Entities hovered array will be the returned array
                        entities = walls;
                    }
                    break;
                }

                // Walls in general (corners and walls)
                case UiSearchEntity.searchWalls: {
                    // Get walls are corner first
                    let walls = wallManager.getWallsAtCorner(cursorPosition, currentFloor, SavaneConstants.PositionTolerance)!;

                    // If any wall, remove temporary walls
                    if (walls !== null) {
                        for (let j = walls.length - 1; j >= 0; j--) {
                            if (walls[j].isTemporary()) {
                                walls.splice(j, 1);
                            }
                        }
                    }
                    // Any wall remaining ?
                    if (walls !== null && walls.length !== 0) {
                        // entitieHovered array will be walls array
                        entities = walls;
                    }
                    else {
                        // Try to find single walls underneath the cursor
                        walls = wallManager.getWallsAtPosition(cursorPosition, currentFloor, SavaneConstants.PositionTolerance, -1) || [];

                        // Any wall found, remove any temporary wall
                        if (walls !== null) {
                            for (let j = walls.length - 1; j >= 0; j--) {
                                if (walls[j].isTemporary()) {
                                    walls.splice(j, 1);
                                }
                            }
                        }

                        // Any wall remaining ?
                        if (walls !== null && walls.length !== 0) {
                            // entitieHovered array will be walls array
                            entities = walls;
                        }
                    }
                    break;
                }

                // User pictures in the scene
                case UiSearchEntity.searchUserPicture:
                    //Only check picture click if they are displayed
                    if (cocosplanManager.showUserPictures) {
                        // Get all user pictures from scene
                        let cameras = currentFloor.getChildren([SceneConstants.EntityType.UserPicture]) as RenderCamera[];
                        // Check collisions
                        let camera = this.collisionWithCursor(cameras, cocosPosition);

                        // Collision with cursor, push the camera into the entities hovered array
                        entities = camera.concat();
                    }
                    break;

                // Render cameras
                case UiSearchEntity.searchRenderCamera: {
                    // Get all render cameras from the scene
                    let cameras = currentFloor.getChildren([SceneConstants.EntityType.RenderCamera]) as RenderCamera[];
                    // Check collisions
                    let camera = this.collisionWithCursor(cameras, cocosPosition);

                    // Collision with cursor, push the camera into the entities hovered array
                    entities = camera.concat();
                    break;
                }

                // Render cameras
                case UiSearchEntity.searchPhotoRenderCamera: {
                    // Get all render cameras from the scene
                    let cameras = currentFloor.getChildren([SceneConstants.EntityType.RenderCamera]) as RenderCamera[];

                    // Filter non photo render cameras
                    for (let j = cameras.length - 1; j >= 0; j--) {
                        if (cameras[j].cameraType !== SceneConstants.CameraType.PhotoRender) {
                            cameras.splice(j, 1);
                        }
                    }

                    // Check collisions
                    let camera = this.collisionWithCursor(cameras, cocosPosition);

                    // Collision with cursor, push the camera into the entities hovered array
                    entities = camera.concat();
                    break;
                }

                // Additional camera for photo render
                case UiSearchEntity.searchAdditionalRenderCamera: {
                    // Get all render cameras from the scene
                    let cameras = currentFloor.getChildren([SceneConstants.EntityType.RenderCamera]) as RenderCamera[];
                    let photoCameraFound = false;
                    // Filter check if any photo render camera
                    for (let j = 0; j < cameras.length ; j++) {
                        if (cameras[j].cameraType === SceneConstants.CameraType.PhotoRender) {
                            photoCameraFound = true;
                            break;
                        }
                    }

                    // If there is at least one photo camera, filter them
                    if (photoCameraFound) {
                        for (let j = cameras.length - 1; j >= 0; j--) {
                            if (cameras[j].cameraType === SceneConstants.CameraType.PhotoRender) {
                                cameras.splice(j, 1);
                            }
                        }

                        // Check collisions
                        let camera = this.collisionWithCursor(cameras, cocosPosition);
                        // Collision with cursor, push the camera into the entities hovered array
                        entities = camera.concat();
                    }
                    break;
                }

                // Comment
                case UiSearchEntity.searchComment:
                    // Get all ligths from scene
                    let comments = currentFloor.getChildren([SceneConstants.EntityType.Comment]);
                    // Check collisions
                    let comment = this.collisionWithCursor(comments, cocosPosition);

                    // Collision, push the camera into the entitiesHovered array
                    entities = comment.concat();
                    break;

                // Arrangement objects
                case UiSearchEntity.searchGeometryPrimitive:
                    let geometry = arrangementManager.getGeometryPrimitiveAtPosition(unsnappedCursorPosition, currentFloor);
                    let group = arrangementManager.getGeometryGroupAtPosition(unsnappedCursorPosition, currentFloor);

                    if (group) {
                        if (geometry && geometry.maxHeight !== null && geometry.maxHeight !== undefined) {
                            if (geometry.maxHeight > (group.maxHeight || 0)) {
                                entities.push(geometry);
                            }
                            else {
                                entities.push(group);
                            }
                        }
                        else {
                            entities.push(group);
                        }
                    }
                    else if (geometry !== null && !geometry.isTemporary()) {
                        // And not hide axo
                        if (!cocosplanManager._hideAxo || !geometry.hideInAxo) {
                            // Push it into hovered entities array
                            entities.push(geometry);
                        }
                    }
                    break;

                // Arrangement objects and groups
                case UiSearchEntity.searchArrangements:
                    let arrangementObject = arrangementManager.getArrangementObjectAtPosition(unsnappedCursorPosition, currentFloor) as ArrangementObject;
                    let arrangementGroup = arrangementManager.getArrangementGroupAtPosition(unsnappedCursorPosition, currentFloor)as ArrangementGroup;

                    if (this.altPressed && arrangementObject !== null) {
                        // Push it into hovered entities array
                        entities.push(arrangementObject);
                    }
                    else {
                        if (arrangementGroup) {
                            // If shift is pressed get deepest arrangement so we can take the deepest element
                            if (this.deepPressed) {
                                let child = arrangementManager.getArrangementGroupAtPosition(unsnappedCursorPosition, arrangementGroup);

                                while (child != null && child.isArrangementGroupEntity()) {
                                    arrangementGroup = child;
                                    child = arrangementManager.getArrangementGroupAtPosition(unsnappedCursorPosition, arrangementGroup);
                                }
                            }

                            if (arrangementObject) {
                                if (arrangementObject.maxHeight > arrangementGroup.maxHeight) {
                                    entities.push(arrangementObject);
                                }
                                else {
                                    entities.push(arrangementGroup);
                                }
                            }
                            else {
                                entities.push(arrangementGroup);
                            }
                        }
                        else {
                            if (arrangementObject) {
                                entities.push(arrangementObject);
                            }
                        }
                    }
                    break;

                // Technical elements
                case UiSearchEntity.searchTechnicalElement:
                    let technicalElement = arrangementManager.getTechnicalElementAtPosition(unsnappedCursorPosition, currentFloor) as TechnicalElement;

                    // Technical element found and not temporary ?
                    if (technicalElement !== null && !technicalElement.isTemporary()) {
                        // And not hide axo
                        if (!cocosplanManager._hideAxo) {
                            // Push it into entities hovered array
                            entities.push(technicalElement);
                        }
                        else {
                            // No collision with axo elements if we hide axo elements
                            if (!(technicalElement as Staircase).hideInAxo)
                            {
                                if (technicalElement.objectId !== SceneConstants.TechnicalElementType.rosette &&
                                    technicalElement.objectId !== SceneConstants.TechnicalElementType.beam &&
                                    technicalElement.objectId !== SceneConstants.TechnicalElementType.frame &&
                                    technicalElement.objectId !== SceneConstants.TechnicalElementType.spotLight &&
                                    technicalElement.objectId !== SceneConstants.TechnicalElementType.airConditioner &&
                                    technicalElement.entityType !== SceneConstants.EntityType.Staircase) {
                                    // Push it into entities hovered array
                                    entities.push(technicalElement);
                                }
                            }
                        }
                    }
                    break;

                // Joineries
                case UiSearchEntity.searchJoinery:
                    let joinery = joineryManager.getJoineryAtPosition(cursorPosition, SavaneConstants.PositionTolerance, currentFloor);

                    // Joinery found and not temporary ?
                    if (joinery !== null && !joinery.isTemporary()) {
                        if (!cocosplanManager._hideAxo || joinery.joineryType != SceneConstants.JoineryType.velux) {
                            // Push it into the entities hovered array
                            entities.push(joinery);
                        }
                    }
                    break;

                // Rooms
                case UiSearchEntity.searchRoom: {
                    let roomHovered = roomManager.getRoomAtPosition(cursorPosition, currentFloor);

                    // Any room found ?
                    if (roomHovered !== null) {
                        // Push it into the entities hovered array
                        entities.push(roomHovered);
                    }
                    break;
                }

                case UiSearchEntity.searchFunctionnalityChip: {
                    // get the rooms underneath the cursor
                    let roomHovered = roomManager.getRoomAtPosition(cursorPosition, currentFloor);

                    // If any room underneath the cursor
                    if (roomHovered !== null) {
                        // Check if a chip is hovered
                        let funcs = roomHovered.functionalityChips;
                        let hoverChip = null;

                        // Parse all chips and check collision with each of them
                        for (let j = 0; j < funcs.length; j++) {
                            let chip = funcs[j];
                            if (chip.isOnPlan && cocosPosition !== undefined) {
                                let name = /*'FunctionalityChip' +*/ chip.id;
                                let chipSprite = Utils.getChildByNameRecursivly(cocosplanManager.scrollWindow.grid, name.toString());
                                if (chipSprite !== null) {
                                    let posInChip = chipSprite.convertToNodeSpace(cocosPosition);
                                    let rect = new cc.Rect(0, 0, chipSprite.getTextureRect().width, chipSprite.getTextureRect().height);
                                    if (cc.rectContainsPoint(rect, posInChip)) {
                                        hoverChip = chip;
                                    }
                                }
                            }
                        }

                        // If no chip hoevred then the room is hovered
                        if (hoverChip !== null) {
                            // Otherwise the chip is hovered
                            entities.push(hoverChip);
                        }
                    }
                    break;
                }
            }

            // If at least one entity is found, then break the loop
            if (entities.length !== 0) {
                break;
            }
        }

        // Return the entity array
        return (entities);
    }

    // Deactivate the previous hovering for previous objects hovered
    deactivatePreviousHovering() {
        // Deactivate hovering for previous entities if any
        if (this.hoverEntities.length !== 0) {
            if (this.hoverType === HoverType.savaneEntities) {
                switch (this.hoverEntities[0].entityType) {
                    case SceneConstants.EntityType.Wall:
                        this.mouseLeaveHoverWall(this.hoverEntities as Wall[], this.cursorPosition);
                        break;

                    case SceneConstants.EntityType.Joinery:
                        this.mouseLeaveHoverJoinery(this.hoverEntities[0] as Joinery, this.cursorPosition);
                        break;

                    case SceneConstants.EntityType.TechnicalElement:
                        this.mouseLeaveHoverTech(this.hoverEntities[0] as TechnicalElement, this.cursorPosition);
                        break;

                    case SceneConstants.EntityType.Staircase:
                        this.mouseLeaveHoverTech(this.hoverEntities[0] as Staircase, this.cursorPosition);
                        break;

                    case SceneConstants.EntityType.Room:
                        this.mouseLeaveHoverRoom(this.hoverEntities[0] as Room, this.cursorPosition);
                        break;

                    case SceneConstants.EntityType.RenderCamera:
                    case SceneConstants.EntityType.ArrangementObject:
                    case SceneConstants.EntityType.ArrangementGroup:
                    case SceneConstants.EntityType.UserPicture:
                    case SceneConstants.EntityType.Comment:
                    case SceneConstants.EntityType.GeometryPrimitive:
                    case SceneConstants.EntityType.GeometryGroup:
                        this.mouseLeaveHoverEntity(this.hoverEntities[0]);
                        break;
                }
            }
        }
    }

    getUiState(): number {
        return -1;
    }

    // Activate the new hovering for new objects hovered
    activateNewHovering() {
        switch (this.hoverEntities[0].entityType) {
            case SceneConstants.EntityType.Wall:
                this.mouseHoverWall(this.hoverEntities as Wall[], this.cursorPosition);
                break;

            case SceneConstants.EntityType.Joinery:
                this.mouseHoverJoinery(this.hoverEntities[0] as Joinery, this.cursorPosition);
                break;

            case SceneConstants.EntityType.TechnicalElement:
                this.mouseHoverTech(this.hoverEntities[0] as TechnicalElement, this.cursorPosition);
                break;

            case SceneConstants.EntityType.Staircase:
                this.mouseHoverTech(this.hoverEntities[0] as Staircase, this.cursorPosition);
                break;

            case SceneConstants.EntityType.Room:
                this.mouseHoverRoom(this.hoverEntities[0] as Room, this.cursorPosition);
                break;

            case SceneConstants.EntityType.ArrangementObject:
            case SceneConstants.EntityType.ArrangementGroup:
            case SceneConstants.EntityType.RenderCamera:
            case SceneConstants.EntityType.UserPicture:
            case SceneConstants.EntityType.Comment:
            case SceneConstants.EntityType.GeometryPrimitive:
            case SceneConstants.EntityType.GeometryGroup:
                this.mouseHoverEntity(this.hoverEntities[0], this.cursorPosition);
                break;
        }
    }

    // Determine the hovered entity depending on current action
    setHover() {
        // No entity hovered yet so empty array
        let entitiesHovered: Entity[] = [];
        // Entity types we are going to search
        let typeArray: number[] = [];
        // For the moment, no entity hovered yet
        let type = HoverType.noEntities;

        // Get new entities hovered first depending on current action in progress
        switch (PlanManager.getInstance().getAction()) {
            // Addor edit joineries, only walls can be hovered
            case ActionStateEnum.addJoinery:
            case ActionStateEnum.editJoinery:
                typeArray = [UiSearchEntity.searchWallsWithoutCorner];
                break;

            // For those states we only hover walls
            case ActionStateEnum.createMultipleWalls:
            case ActionStateEnum.idleMultipleCreates:
            case ActionStateEnum.createWall:
            case ActionStateEnum.editPoint:
            case ActionStateEnum.idleCreate:
                typeArray = [UiSearchEntity.searchWalls];
                break;

            // User picture placement, only user pictures can be hoevred
            case ActionStateEnum.idleCamera:
                typeArray = [UiSearchEntity.searchUserPicture];
                break;

            // Idle visualisation, entity edition, we react to (in priority order) :
            // Render cameras, Lights, Arrangement objects, Technical elements,
            // Joineries,      Walls,  User Pictures        Room
            case ActionStateEnum.idleVisualise:
            case ActionStateEnum.EditEntity:
                typeArray = [UiSearchEntity.searchRenderCamera, UiSearchEntity.searchArrangements,
                UiSearchEntity.searchJoinery, UiSearchEntity.searchWallsWithoutCorner, UiSearchEntity.searchTechnicalElement, UiSearchEntity.searchUserPicture, UiSearchEntity.searchRoom];
                break;

            // Idle Decoration, we react to (in priority order) :
            // Render cameras, Lights, Arrangement objects, Technical elements,
            // Joineries,      Walls,  User Pictures        Room
            case ActionStateEnum.idleDecoration:
                if (PlanManager.getInstance()._hideArrangements) {
                    typeArray = [UiSearchEntity.searchRenderCamera, UiSearchEntity.searchFunctionnalityChip,
                    UiSearchEntity.searchJoinery, UiSearchEntity.searchWallsWithoutCorner, UiSearchEntity.searchTechnicalElement, UiSearchEntity.searchUserPicture, UiSearchEntity.searchGeometryPrimitive, UiSearchEntity.searchRoom];
                }
                else {
                    typeArray = [UiSearchEntity.searchRenderCamera, UiSearchEntity.searchFunctionnalityChip, UiSearchEntity.searchArrangements,
                    UiSearchEntity.searchJoinery, UiSearchEntity.searchWallsWithoutCorner, UiSearchEntity.searchTechnicalElement, UiSearchEntity.searchUserPicture, UiSearchEntity.searchGeometryPrimitive, UiSearchEntity.searchRoom];
                }
                break;

            // Nothing can be hovered in this action states
            case ActionStateEnum.addRoom:
            case ActionStateEnum.AddEntity:
            case ActionStateEnum.editWall:
            case ActionStateEnum.EditCote:
            case ActionStateEnum.MoveFunctionalityChipState:
            case ActionStateEnum.AddTechnicalElement:
            case ActionStateEnum.EditTechnicalElement:
            case ActionStateEnum.MoveTechnicalElement:
            case ActionStateEnum.AddStaircase:
            case ActionStateEnum.EditStaircase:
                type = HoverType.noEntities;
                break;

            // Only rooms can be hovered in this action state
            // case ActionStateEnum.idleSmartDesigner:
            //     typeArray = [UiSearchEntity.searchRoom];
            //     break;

            // Idle room validation, construction, quotation, selectOrderRoom, we react to (in priority order) :
            // User Pictures, Technical elements,  Joineries,
            // Walls,         Functionnality chip, Room
            case ActionStateEnum.RoomsValidation:
            case ActionStateEnum.Quotation:
                typeArray = [UiSearchEntity.searchUserPicture, UiSearchEntity.searchComment, UiSearchEntity.searchJoinery,
                UiSearchEntity.searchWalls, UiSearchEntity.searchTechnicalElement, UiSearchEntity.searchFunctionnalityChip, UiSearchEntity.searchRoom];
                break;

            case ActionStateEnum.SelectedOrderRoom:
                typeArray = [UiSearchEntity.searchRoom];
                break;

            case ActionStateEnum.IdleConstruction:
                typeArray = [UiSearchEntity.searchComment, UiSearchEntity.searchJoinery, UiSearchEntity.searchRenderCamera,
                UiSearchEntity.searchWalls, UiSearchEntity.searchTechnicalElement, UiSearchEntity.searchFunctionnalityChip, UiSearchEntity.searchGeometryPrimitive, UiSearchEntity.searchRoom, UiSearchEntity.searchUserPicture];
                break;

            default:
                break;
        }

        // Get entities hovered now
        entitiesHovered = this.searchEntity(typeArray, this.cursorPosition, this.unsnappedCursorPosition, this.cocosPosition);

        // Hovered entities found ?
        if (entitiesHovered.length > 0) {
            // Yes
            type = HoverType.savaneEntities;
        }

        // Deactivate hovering for previous entities if any
        this.deactivatePreviousHovering();

        // Store new hovered entity and new hovered type
        this.hoverEntities = entitiesHovered;
        this.hoverType = type;

        // Activate hovering for new entities hovered
        if (this.hoverType === HoverType.savaneEntities) {
            this.activateNewHovering();
        }
    }

    // Display necessary rulers depending on the edition mode
    setRuler() {
        let positions: any;
        // Plan manager and plan manager action shortcuts
        let cocosplanManager = PlanManager.getInstance();
        let cocosplanManagerAction = cocosplanManager.getAction();
        const currentFloor = cocosplanManager.world.currentScene?.currentFloor;

        if (!currentFloor) {
            return;
        }

        if (cocosplanManagerAction === ActionStateEnum.editPoint || cocosplanManagerAction === ActionStateEnum.createWall || cocosplanManagerAction === ActionStateEnum.createMultipleWalls) {
            //Create array of ignored points
            let ignoredPoints = [(cocosplanManager.state as EditPointState).editedPoint];
            if (this.hoverType === HoverType.savaneEntities && this.hoverEntities.length > 0 && this.hoverEntities[0].isWallEntity()) {
                //Ignore points of selected walls
                for (let i = 0; i < this.hoverEntities.length; i++) {
                    ignoredPoints.push((this.hoverEntities[i] as Wall).begin);
                    ignoredPoints.push((this.hoverEntities[i] as Wall).end);
                }
            }
            positions = planManager.getAlignement(this.cursorPosition, Utils.magnetDistance, ignoredPoints, currentFloor);
        }
        else {
            if (cocosplanManagerAction === ActionStateEnum.addRoom) {
                positions = planManager.getAlignement(this.cursorPosition, Utils.magnetDistance, (cocosplanManager.state as AddRoomState).getRoomPoints(), currentFloor);
            }
            else {
                positions = planManager.getAlignement(this.cursorPosition, Utils.magnetDistance, null, currentFloor);
            }
        }

        // For the moment no ruller displayed
        this.verticalRuler = false;
        this.horizontalRuler = false;

        if (this.hoverType !== HoverType.savaneEntities || this.hoverEntities.length === 0 || !this.hoverEntities[0].isWallEntity()) {
            if (positions[0] !== null) {
                this.cursorPosition[0] = positions[0][0];
                this.verticalRuler = true;
            }
            else {
                this.verticalRuler = false;
            }
            if (positions[1] !== null) {
                this.cursorPosition[1] = positions[1][1];
                this.horizontalRuler = true;
            }
            else {
                this.horizontalRuler = false;
            }
        }
        else {
            if (this.hoverType === HoverType.savaneEntities && this.hoverEntities.length === 1 && this.hoverEntities[0] instanceof Wall) {
                // If we are hovering a wall segment start or end, don't go further (the code below would introduce inaccuracy that might create a weird corner with 2 walls)
                if (!this.hoverEntities[0].isBegin(this.cursorPosition) && !this.hoverEntities[0].isEnd(this.cursorPosition)) {
                    let wall = this.hoverEntities[0];
                    let secondPoint, point;
                    if (wall !== null) {
                        let wallSegment = wall.segment;

                        if (positions[0] !== null && Math.abs(wall.wallDirection[0]) > 50 * SavaneConstants.Tolerance) {
                            //Horizontal wall
                            secondPoint = math.vec3.clone(positions[0]);
                            secondPoint[1] += 500;
                            point = SavaneMath.getCrossPoint(positions[0], secondPoint, wallSegment.begin, wallSegment.end);
                            if (point !== null) {
                                if (this.getUiState() !== UiStateEnum.uiCutDimensionState && this.getUiState() !== UiStateEnum.uiCutAndRulerState) {
                                    point[1] = this.cursorPosition[1];
                                }
                                this.cursorPosition = point;
                                this.verticalRuler = true;
                            }
                        }
                        else {
                            if (positions[1] !== null && Math.abs(wall.wallDirection[1]) > 50 * SavaneConstants.Tolerance) {
                                //Vertical wall
                                secondPoint = math.vec3.clone(positions[1]);
                                secondPoint[0] += 500;
                                point = SavaneMath.getCrossPoint(positions[1], secondPoint, wallSegment.begin, wallSegment.end);
                                if (point !== null) {
                                    if (this.getUiState() !== UiStateEnum.uiCutDimensionState && this.getUiState() !== UiStateEnum.uiCutAndRulerState) {
                                        point[0] = this.cursorPosition[0];
                                    }
                                    this.cursorPosition = point;
                                    this.horizontalRuler = true;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * The mouse is currently over a wall
     */
    mouseHoverWall(wallHovered: Wall[], cursorPosition: math.vec3): boolean {
        return true;
    }

    /**
     * The mouse just get out of a wall
     */
    mouseLeaveHoverWall(wallHovered: Wall[], cursorPosition: math.vec3) {
        return true;
    }

    /**
     * The mouse is currently over a technical element
     */
    mouseHoverTech(tech: TechnicalElement, cursorPosition: math.vec3): boolean {
        return true;
    }

    /**
     * The mouse just get out of a technical element
     */
    mouseLeaveHoverTech(tech: TechnicalElement, cursorPosition: math.vec3): boolean {
        return true;
    }

    /**
     * The mouse is currently over a joinery
     */
    mouseHoverJoinery(joinery: Joinery, cursorPosition: math.vec3): boolean {
        return true;
    }

    /**
     * The mouse just get out of a joinery
     */
    mouseLeaveHoverJoinery(joinery: Joinery, cursorPosition: math.vec3): boolean {
        return true;
    }

    /**
     * The mouse is currently over a room
     */
    mouseHoverRoom(room: Room, cursorPosition: math.vec3): boolean {
        return true;
    }

    /**
     * The mouse just get out of a room
     */
    mouseLeaveHoverRoom(room: Room, cursorPosition: math.vec3): boolean {
        return true;
    }

    /**
     * The mouse is currently over an entity
     */
    mouseHoverEntity(entityHovered: Entity, cursorPosition: math.vec3): boolean {
        return true;
    }

    /**
     * The mouse just get out of an entity
     */
    mouseLeaveHoverEntity(entityHovered: Entity): boolean {
        return true;
    }

    // Key pressed
    keyPressed(keyCode: number): UiState | null {
        if (keyCode === cc.KEY.ctrl) {
            this.ctrlPressed = true;
        }
        if (keyCode === cc.KEY.alt) {
            this.altPressed = true;
        }
        if (keyCode === 226) {
            this.deepPressed = true;
        }
        // Pour tests
        if (keyCode === cc.KEY.f6) {
            eventsManager.instance.dispatch(Events.DESIGNER_FILTERS_NEXT_OBJECTS, {
                position: this.cursorPosition,
                floor: JSONSerializer.serializeEntity(PlanManager.getInstance().world.currentScene!.currentFloor!)
            });
        }
        // Fin pour tests
        if (keyCode === cc.KEY.f3) {
            PlanManager.getInstance()._wallWireframe = !PlanManager.getInstance()._wallWireframe;
            eventsManager.instance.dispatch(Events.WALL_SET_TRANSPARENCY);
        }
        if (keyCode === cc.KEY.f2) {
            PlanManager.getInstance()._hideAxo = !PlanManager.getInstance()._hideAxo;
            eventsManager.instance.dispatch(Events.HIDE_AXO);
        }
        if (keyCode === cc.KEY.f1) {
            PlanManager.getInstance()._hideArrangements = !PlanManager.getInstance()._hideArrangements;
            eventsManager.instance.dispatch(Events.HIDE_ARRANGEMENTS);
        }
        if (keyCode === cc.KEY.h) {
            eventsManager.instance.dispatch(Events.SHOW_DETAILED_HEIGHT, true);
        }

        return null;
    }

    keyUnPressed(keyCode: number): UiState | null {
        if (keyCode === cc.KEY.ctrl) {
            this.ctrlPressed = false;
        }
        if (keyCode === cc.KEY.alt) {
            this.altPressed = false;
        }
        if (keyCode === 226) {
            this.deepPressed = false;
        }
        if (keyCode === cc.KEY.h) {
            eventsManager.instance.dispatch(Events.SHOW_DETAILED_HEIGHT, false);
        }
        return null;
    }
    moveMouseCursor(cursorPosition: math.vec3, cocosPos: math.vec3): UiState | null {
        return null;
    }

    /**
     * Dispose elements of the state
     */
    dispose() {
        // Deactivate previous hovering to cleanup this state
        this.deactivatePreviousHovering();
    }
}
